import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { RouteFactory } from "@app/routePaths";
import logoBTGLightLg from "@assets/logo-btg-light-lg.svg";
import { BlockTitle } from "@blocks/Blocks";
import NoAdSettings from "@components/Advertisement/NoAdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import HomepageMasthead from "@components/Header/HomepageMasthead";
import MetaTags from "@components/MetaTags/MetaTags";
import MyBTHomeCarousel from "@components/MyBT/MyBTHomeCarousel";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { myBTRecommendedKeywords } from "@features/mybt/constants";
import { useBTGHomepageRedirect } from "@hooks/useBTGHomepageRedirect";
import { useWindowSize } from "@hooks/useWindowSize";
import BTGlobalNewsletter from "@pages/Article/components/ArticleNewsletter/BTGlobalNewsletter";
import ByCountry from "@pages/Global/components/ByCountry";
import { GLOBAL_NAVIGATION } from "@pages/Global/utils/constants";
import { GlobalContext } from "@pages/Global/utils/types";
import TemplateA from "@particles/card/templates/TemplateA/TemplateA";
import TemplateB from "@particles/card/templates/TemplateB/TemplateB";
import TemplateC from "@particles/card/templates/TemplateC/TemplateC";
import TemplateD from "@particles/card/templates/TemplateD/TemplateD";
import TemplateE from "@particles/card/templates/TemplateE/TemplateE";
import TemplateF from "@particles/card/templates/TemplateF/TemplateF";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { cn, getGlobalPageJSONLD } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

type GlobalPageContentProps = Pick<GlobalContext, "data">;

/**
 * GlobalPageContent component renders the main content for the Global page.
 * It includes sections for top stories, skybox, economy policy, ASEAN business,
 * companies, tech, and a footer with SEO data.
 * Figma: https://www.figma.com/design/rR8akVWS3SksIyaOSqetu4/BAU--WEB-?node-id=11666-5494&m=dev
 */
const GlobalPageContent = ({
  data: {
    title,
    topStoriesProps,
    singaporeProps,
    internationalProps,
    btPlusProps,
    featuredProps,
    aseanProps,
    opinionProps,
    myBTArticles,
    seoFooterData,
  },
}: GlobalPageContentProps): React.ReactElement => {
  useBTGHomepageRedirect();

  const sendDataToGTM = useGTMDispatch();
  const userInfo = useOKTAUserStore((state) => state.userInfo);
  const adBlockDetected = useDetectAdBlock();
  const { isScreenLG } = useWindowSize();

  const [isFeaturedStoryVisible, setIsFeaturedStoryVisible] = useState(true);
  const { ref } = useInView({
    threshold: 0.1,
    onChange(inView) {
      if (!isScreenLG) return;

      setIsFeaturedStoryVisible(inView);
    },
  });

  return (
    <div
      className="w-full overflow-x-clip text-gray-850"
      data-testid="global-home-content-component"
    >
      <NoAdSettings />

      <Header
        isFromHome
        headerData={GLOBAL_NAVIGATION}
        className={cn(
          "border-gray-850 bg-gray-925 py-2.5 transition-colors duration-300 lg:py-0",
          { "border-gray-175 bg-white": !isFeaturedStoryVisible && isScreenLG }
        )}
        textClassName={cn("text-gray-250 lg:text-gray-850", {
          "lg:text-gray-550 lg:hover:text-gray-550 lg:hover:bg-gray-850":
            isFeaturedStoryVisible && isScreenLG,
        })}
        borderClassName={cn({
          "border-gray-850": isFeaturedStoryVisible && isScreenLG,
        })}
        parentCategory={RouteFactory.global.replace("/", "")}
        editionRef="global-hamburger-menu-edition-switch"
        iconsBgClassName={cn({
          "lg:hover:bg-gray-850": isFeaturedStoryVisible && isScreenLG,
        })}
        subscribeClassName={cn({
          "border-orange-300 bg-gray-925 border lg:text-orange-300 hover:bg-orange-300 hover:text-white":
            isFeaturedStoryVisible && isScreenLG,
        })}
        userIconStrokeColor={cn("border-gray-850", {
          "border-gray-175": !isFeaturedStoryVisible && isScreenLG,
        })}
        isDisplayEditionSwitcher
      />

      <HomepageMasthead
        className="bg-gray-925"
        src={logoBTGLightLg}
        imgWidth={475}
        imgHeight={77}
        theme="dark"
        title="The Business Times Global Edition"
        editionRef="global-header-edition-switch"
        isDisplayEditionSwitcher
        isDisplayH1
      />

      <MetaTags
        title={title}
        ogTitle={title}
        description="The Business Times Global Edition - Trusted Asia business news and analysis. Get regional perspectives on Southeast Asia and Greater China's markets and economies."
        ogDescription="The Business Times Global Edition - Trusted Asia business news and analysis. Get regional perspectives on Southeast Asia and Greater China's markets and economies."
        ogType="website"
        articlePageclass="frontpage"
        slug={RouteFactory.global}
        keywords={[
          "singapore budget 2015",
          "singapore",
          "budget 2015",
          "business & financial news",
        ]}
        imageAlt="The Business Times Global Edition"
        robots="index, follow, noarchive"
        googlebot="index, nofollow, noarchive"
        alternates={[
          { hrefLang: "en-sg", href: "https://www.businesstimes.com.sg/" },
          { hrefLang: "en", href: "https://www.businesstimes.com.sg/global" },
          { hrefLang: "x-default", href: "https://www.businesstimes.com.sg/" },
        ]}
        ogLocale="en_US"
        jsonLd={getGlobalPageJSONLD()}
      />

      <GAData
        title="BT Global Homepage"
        level2="home"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <div ref={ref}>
        <TemplateC
          cardProps={[topStoriesProps[0]]}
          className="mb-4 flex w-full items-center justify-center bg-gray-925 md:mb-8"
        />
      </div>

      <Container rootClassName="px-5 lg:px-3">
        <TemplateE
          cardProps={topStoriesProps.slice(1, 6)}
          className="mb-14 md:mb-15"
        />

        <TemplateB
          header={
            <>
              <BlockTitle
                text="BT Plus"
                rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center"
              />
              <p className="mb-8 text-center font-poppins text-base text-blue-160">
                <em>The best insights across SPH Media</em>
              </p>
            </>
          }
          cardProps={btPlusProps}
          className="mb-16 border-t-2 border-blue-100 pt-3"
        />

        <TemplateF
          header={
            <BlockTitle
              text="Opinion"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center mb-8"
            />
          }
          cardProps={opinionProps}
          className="mb-16 border-t-2 border-blue-100 pt-3"
        />

        <TemplateA
          header={
            <BlockTitle
              text="Singapore"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl mb-8 text-center"
            />
          }
          className="mb-8 border-t-2 border-blue-100 pt-3"
          cardProps={singaporeProps}
        />

        <div className="lg:border-t-2 lg:border-blue-100 lg:pt-6">
          <BTGlobalNewsletter
            className="mb-8"
            dataSectionName="global-newsletter-1"
            onSubmitCallback={() => {
              sendDataToGTM({
                event: "custom_event",
                eventCategory: "global-newsletter-1",
                eventAction: "click",
                eventLabel: "submit",
              });
            }}
            onSuccessCallback={() => {
              sendDataToGTM({
                event: "custom_event",
                eventCategory: "global-newsletter-1",
                eventAction: "click",
                eventLabel: "successful submit",
              });
            }}
          />
        </div>

        <TemplateC
          cardProps={featuredProps}
          className="-mx-5 mb-8 w-[calc(100%+2.5rem)] bg-gray-925 md:mx-0 md:w-full"
        />

        <TemplateD
          header={
            <BlockTitle
              text="Asean"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center col-span-1 lg:col-span-4"
            />
          }
          cardProps={aseanProps}
          className="col-span-3 mb-16 border-t-2 border-blue-100 pt-3"
          rightSidebar={<ByCountry />}
        />

        <TemplateA
          header={
            <BlockTitle
              text="International"
              rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl mb-8 text-center"
            />
          }
          className="mb-16 border-t-2 border-blue-100 pt-3"
          cardProps={internationalProps}
        />

        <>
          {typeof userInfo?.loginid !== "undefined" ? (
            <MyBTHomeCarousel
              results={myBTArticles}
              keywords={myBTRecommendedKeywords}
              title="INTRODUCING"
              showTooltip={false}
              rootClassName="mb-16 border-t-2 border-blue-100 pt-3"
              disablePaywall={
                userInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
              }
              testId="global-mybt"
              header={
                <BlockTitle
                  text="myBT"
                  rootClassName="text-blue-175 font-freightDisplayPro font-normal !text-10xl text-center"
                />
              }
              arrowWrapperClassName={cn("-top-10")}
              slideWrapperClassName={cn("border-blue-100")}
              dividerClassName={cn("divide-blue-100")}
              iconClassName={cn("text-verticals-btblue")}
              refCode="global-mybt"
            />
          ) : null}
        </>

        <div className="lg:border-t-2 lg:border-blue-100 lg:pt-6">
          <BTGlobalNewsletter
            className="mb-8"
            dataSectionName="global-newsletter-2"
            onSubmitCallback={() => {
              sendDataToGTM({
                event: "custom_event",
                eventCategory: "global-newsletter-2",
                eventAction: "click",
                eventLabel: "submit",
              });
            }}
            onSuccessCallback={() => {
              sendDataToGTM({
                event: "custom_event",
                eventCategory: "global-newsletter-2",
                eventAction: "click",
                eventLabel: "successful submit",
              });
            }}
          />
        </div>

        <SeoFooter data={seoFooterData} className="px-0" />
      </Container>
    </div>
  );
};

export default GlobalPageContent;
